import { SkyButton, SkyMessageBar } from "@skypointcloud/skypoint-ui";
import { useLocation } from "wouter";
import { idParamsSchema } from "@shared/types/common";
import { ApiClientError } from "@/lib/api";
import { absolutePath } from "@/lib/routes";

/** True when the id could never name a record: the server would refuse it as malformed (#1652). */
export function isMalformedRecordId(id: string): boolean {
  return !idParamsSchema.safeParse({ id }).success;
}

/** True when the server answered that the record is not in this instance. */
export function isNotFoundError(error: unknown): boolean {
  return error instanceof ApiClientError && error.status === 404;
}

/**
 * #1652: a link to a record that is not there reads the same whether the id is badly formed or
 * well formed and missing: one sentence and the way back to the list. No Try again, since
 * asking again can never find it.
 */
export function RecordNotFound({
  noun,
  listLabel,
  listPath,
  testId,
}: {
  /** "run", "connection" or "schedule": lower case, in "That {noun} was not found". */
  noun: string;
  /** The button's words, "Back to Runs". */
  listLabel: string;
  /** The list's path inside the instance. */
  listPath: string;
  testId: string;
}) {
  const [, navigate] = useLocation();
  const heading = `${noun.charAt(0).toUpperCase()}${noun.slice(1)} not found`;
  return (
    <SkyMessageBar
      intent="warning"
      layout="multiline"
      title={heading}
      message={`That ${noun} was not found in this instance.`}
      actions={
        <SkyButton
          size="small"
          appearance="secondary"
          onClick={() => navigate(absolutePath(listPath))}
          testId={`${testId}-back`}
        >
          {listLabel}
        </SkyButton>
      }
      testId={testId}
    />
  );
}
